import React, { useState, useRef, useEffect } from 'react';
import './CoachMode.css';
import CustomDropdown from './CustomDropdown';
import { HINDI_PHONEMES } from '../data/hindiPhonemes';

// Define phoneme prompts for practice
const ENGLISH_PHONEME_PROMPTS = [
  // Vowel sounds
  { id: 'vowel_i', symbol: '/i:/', example: 'beat', instruction: 'Say "eee" as in "beat"' },
  { id: 'vowel_I', symbol: '/ɪ/', example: 'bit', instruction: 'Say "i" as in "bit"' },
  { id: 'vowel_e', symbol: '/e/', example: 'bet', instruction: 'Say "e" as in "bet"' },
  { id: 'vowel_ae', symbol: '/æ/', example: 'bat', instruction: 'Say "a" as in "bat"' },
  { id: 'vowel_a', symbol: '/ɑ:/', example: 'father', instruction: 'Say "ah" as in "father"' },
  { id: 'vowel_o', symbol: '/ɔ:/', example: 'bought', instruction: 'Say "aw" as in "bought"' },
  { id: 'vowel_u', symbol: '/u:/', example: 'boot', instruction: 'Say "oo" as in "boot"' },
  { id: 'vowel_U', symbol: '/ʊ/', example: 'put', instruction: 'Say "u" as in "put"' },
  
  // Consonant sounds
  { id: 'consonant_p', symbol: '/p/', example: 'pat', instruction: 'Say "p" as in "pat"' },
  { id: 'consonant_b', symbol: '/b/', example: 'bat', instruction: 'Say "b" as in "bat"' },
  { id: 'consonant_t', symbol: '/t/', example: 'tap', instruction: 'Say "t" as in "tap"' },
  { id: 'consonant_d', symbol: '/d/', example: 'dog', instruction: 'Say "d" as in "dog"' },
  { id: 'consonant_k', symbol: '/k/', example: 'cat', instruction: 'Say "k" as in "cat"' },
  { id: 'consonant_g', symbol: '/g/', example: 'go', instruction: 'Say "g" as in "go"' },
  { id: 'consonant_f', symbol: '/f/', example: 'fan', instruction: 'Say "f" as in "fan"' },
  { id: 'consonant_v', symbol: '/v/', example: 'van', instruction: 'Say "v" as in "van"' },
  { id: 'consonant_th_voiceless', symbol: '/θ/', example: 'think', instruction: 'Say "th" as in "think"' },
  { id: 'consonant_th_voiced', symbol: '/ð/', example: 'this', instruction: 'Say "th" as in "this"' },
  { id: 'consonant_s', symbol: '/s/', example: 'sit', instruction: 'Say "s" as in "sit"' },
  { id: 'consonant_z', symbol: '/z/', example: 'zoo', instruction: 'Say "z" as in "zoo"' },
  { id: 'consonant_sh', symbol: '/ʃ/', example: 'ship', instruction: 'Say "sh" as in "ship"' },
  { id: 'consonant_zh', symbol: '/ʒ/', example: 'measure', instruction: 'Say "zh" as in "measure"' },
  { id: 'consonant_h', symbol: '/h/', example: 'hat', instruction: 'Say "h" as in "hat"' },
  { id: 'consonant_m', symbol: '/m/', example: 'mat', instruction: 'Say "m" as in "mat"' },
  { id: 'consonant_n', symbol: '/n/', example: 'net', instruction: 'Say "n" as in "net"' },
  { id: 'consonant_ng', symbol: '/ŋ/', example: 'sing', instruction: 'Say "ng" as in "sing"' },
  { id: 'consonant_l', symbol: '/l/', example: 'let', instruction: 'Say "l" as in "let"' },
  { id: 'consonant_r', symbol: '/r/', example: 'red', instruction: 'Say "r" as in "red"' },
  { id: 'consonant_w', symbol: '/w/', example: 'wet', instruction: 'Say "w" as in "wet"' },
  { id: 'consonant_y', symbol: '/j/', example: 'yes', instruction: 'Say "y" as in "yes"' }
];

// Convert Hindi phonemes to coach prompts format
const HINDI_PHONEME_PROMPTS = HINDI_PHONEMES.map(phoneme => ({
  id: `hindi_${phoneme.symbol.replace(/[^a-zA-Z0-9]/g, '_')}`,
  symbol: `/${phoneme.symbol}/`,
  example: phoneme.examples[0],
  instruction: `${phoneme.devanagari} - ${phoneme.description}`,
  devanagari: phoneme.devanagari
}));

interface Recording {
  id: string;
  promptId: string;
  audioBlob: Blob;
  audioUrl: string;
  videoBlob?: Blob;
  videoUrl?: string;
  timestamp: Date;
  uploaded: boolean;
}

// Language and accent options
const LANGUAGES = [
  { code: 'English', name: 'English' },
  { code: 'Hindi', name: 'Hindi' },
];

const ACCENTS = {
  English: ['American', 'British', 'Australian', 'Indian', 'Canadian'],
  Hindi: ['Standard', 'Delhi', 'Mumbai', 'Punjabi-influenced'],
};

const CoachMode: React.FC = () => {
  const [recordingMode, setRecordingMode] = useState<'phoneme' | 'sentence'>('phoneme');
  const [selectedLanguage, setSelectedLanguage] = useState('English');
  const [selectedAccent, setSelectedAccent] = useState('American');
  const [selectedPrompt, setSelectedPrompt] = useState(ENGLISH_PHONEME_PROMPTS[0]);
  const [isRecording, setIsRecording] = useState(false);
  const [isVideoEnabled, setIsVideoEnabled] = useState(false);
  const [recordings, setRecordings] = useState<Recording[]>([]);
  const [currentRecording, setCurrentRecording] = useState<Recording | null>(null);
  const [uploadStatus, setUploadStatus] = useState<string>('');
  const [uploadedRecordingId, setUploadedRecordingId] = useState<string | null>(null);
  const [hindiSentences, setHindiSentences] = useState<any[]>([]);
  const [englishSentences, setEnglishSentences] = useState<any[]>([]);
  const [selectedSentence, setSelectedSentence] = useState<any>(null);
  
  const audioRecorderRef = useRef<MediaRecorder | null>(null);
  const videoRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const videoChunksRef = useRef<Blob[]>([]);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    // Cleanup on unmount
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  useEffect(() => {
    // Load sentences when switching to sentence mode
    if (recordingMode === 'sentence') {
      if (selectedLanguage === 'Hindi') {
        fetchHindiSentences();
      } else if (selectedLanguage === 'English') {
        fetchEnglishSentences();
      }
    }
    // Update phoneme prompts when language changes
    if (recordingMode === 'phoneme') {
      const prompts = selectedLanguage === 'Hindi' ? HINDI_PHONEME_PROMPTS : ENGLISH_PHONEME_PROMPTS;
      setSelectedPrompt(prompts[0]);
    }
  }, [recordingMode, selectedLanguage]);

  const fetchHindiSentences = async () => {
    try {
      // Determine backend URL based on environment
      const backendUrl = window.location.hostname === 'localhost' 
        ? 'http://localhost:8080'
        : 'https://voice-coach-backend-406125116283.us-east1.run.app';
      
      // Fetch from database
      const response = await fetch(`${backendUrl}/api/sentences?language=hi-IN&limit=50`);
      if (response.ok) {
        const data = await response.json();
        const sentences = data.sentences.map((s: any) => ({
          id: s.id, // Use database ID
          hindi: s.text,
          transliteration: s.transliteration,
          english: s.translation_en,
          difficulty: s.difficulty,
          referenceAudioUrl: s.reference_audio_url
        }));
        setHindiSentences(sentences);
        if (sentences.length > 0) {
          setSelectedSentence(sentences[0]);
        }
      } else {
        // Fallback to JSON file if API fails
        const jsonResponse = await fetch('/hindi-sentences.json');
        const data = await jsonResponse.json();
        setHindiSentences(data);
        if (data.length > 0) {
          setSelectedSentence(data[0]);
        }
      }
    } catch (error) {
      console.error('Failed to load Hindi sentences:', error);
      // Fallback to JSON file
      try {
        const response = await fetch('/hindi-sentences.json');
        const data = await response.json();
        setHindiSentences(data);
        if (data.length > 0) {
          setSelectedSentence(data[0]);
        }
      } catch (e) {
        console.error('Failed to load Hindi sentences from JSON:', e);
      }
    }
  };

  const fetchEnglishSentences = async () => {
    try {
      // Determine backend URL based on environment
      const backendUrl = window.location.hostname === 'localhost' 
        ? 'http://localhost:8080'
        : 'https://voice-coach-backend-406125116283.us-east1.run.app';
      
      // Fetch from database
      const response = await fetch(`${backendUrl}/api/sentences?language=en-US&limit=50`);
      if (response.ok) {
        const data = await response.json();
        const sentences = data.sentences.map((s: any) => ({
          id: s.id, // Use database ID
          text: s.text,
          targetPhonemes: s.targetPhonemes || [],
          difficulty: s.difficulty,
          type: s.category || 'general'
        }));
        setEnglishSentences(sentences);
        if (sentences.length > 0) {
          setSelectedSentence(sentences[0]);
        }
      } else {
        // Fallback to JSON file if API fails
        const jsonResponse = await fetch('/english-sentences.json');
        const data = await jsonResponse.json();
        setEnglishSentences(data);
        if (data.length > 0) {
          setSelectedSentence(data[0]);
        }
      }
    } catch (error) {
      console.error('Failed to load English sentences:', error);
      // Fallback to JSON file
      try {
        const response = await fetch('/english-sentences.json');
        const data = await response.json();
        setEnglishSentences(data);
        if (data.length > 0) {
          setSelectedSentence(data[0]);
        }
      } catch (e) {
        console.error('Failed to load English sentences from JSON:', e);
      }
    }
  };

  const startRecording = async () => {
    try {
      // Get audio stream
      const audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Get video stream if enabled
      let videoStream: MediaStream | null = null;
      if (isVideoEnabled) {
        videoStream = await navigator.mediaDevices.getUserMedia({ video: true });
        if (videoRef.current && videoStream) {
          videoRef.current.srcObject = videoStream;
        }
      }
      
      // Combine streams
      const combinedStream = new MediaStream();
      audioStream.getTracks().forEach(track => combinedStream.addTrack(track));
      if (videoStream) {
        videoStream.getTracks().forEach(track => combinedStream.addTrack(track));
      }
      streamRef.current = combinedStream;
      
      // Setup audio recorder
      const audioRecorder = new MediaRecorder(audioStream);
      audioRecorderRef.current = audioRecorder;
      audioChunksRef.current = [];
      
      audioRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      audioRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const audioUrl = URL.createObjectURL(audioBlob);
        
        // Create recording object based on mode
        const recordingId = recordingMode === 'phoneme' 
          ? `${selectedPrompt.id}_${Date.now()}`
          : `sentence_${selectedSentence?.id || 'unknown'}_${Date.now()}`;
        const promptId = recordingMode === 'phoneme' 
          ? selectedPrompt.id 
          : `sentence_${selectedSentence?.id || 'unknown'}`;
        
        const recording: Recording = {
          id: recordingId,
          promptId: promptId,
          audioBlob,
          audioUrl,
          timestamp: new Date(),
          uploaded: false
        };
        
        // Add video if it was recorded
        if (isVideoEnabled && videoChunksRef.current.length > 0) {
          const videoBlob = new Blob(videoChunksRef.current, { type: 'video/webm' });
          const videoUrl = URL.createObjectURL(videoBlob);
          recording.videoBlob = videoBlob;
          recording.videoUrl = videoUrl;
        }
        
        setCurrentRecording(recording);
        setRecordings(prev => [...prev, recording]);
      };
      
      // Setup video recorder if enabled
      if (isVideoEnabled && videoStream) {
        const videoRecorder = new MediaRecorder(videoStream);
        videoRecorderRef.current = videoRecorder;
        videoChunksRef.current = [];
        
        videoRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            videoChunksRef.current.push(event.data);
          }
        };
        
        videoRecorder.start();
      }
      
      audioRecorder.start();
      setIsRecording(true);
      
    } catch (error) {
      console.error('Error starting recording:', error);
      alert('Failed to start recording. Please check your permissions.');
    }
  };

  const stopRecording = () => {
    if (audioRecorderRef.current && isRecording) {
      audioRecorderRef.current.stop();
      
      if (videoRecorderRef.current) {
        videoRecorderRef.current.stop();
      }
      
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      
      setIsRecording(false);
    }
  };

  const uploadRecording = async (recording: Recording) => {
    try {
      setUploadStatus('Uploading...');
      
      // Determine backend URL based on environment
      const backendUrl = window.location.hostname === 'localhost' 
        ? 'http://localhost:8080'
        : 'https://voice-coach-backend-406125116283.us-east1.run.app';
      
      let response;
      
      // Check if this is a sentence recording
      if (recording.promptId.startsWith('sentence_')) {
        // Handle sentence recording upload
        const sentenceId = recording.promptId.replace('sentence_', '');
        const sentence = [...hindiSentences, ...englishSentences].find(s => s.id?.toString() === sentenceId || s.id === parseInt(sentenceId));
        
        if (!sentence) {
          throw new Error('Sentence not found');
        }
        
        // Upload to backend
        const formData = new FormData();
        formData.append('audio', recording.audioBlob, 'recording.webm');
        formData.append('sentence_id', sentenceId);
        formData.append('coach_id', 'coach-' + Date.now()); // TODO: Replace with actual coach ID from auth
        
        response = await fetch(`${backendUrl}/api/sentences/submit-recording`, {
          method: 'POST',
          body: formData
        });
        
      } else {
        // Handle phoneme recording upload
        const prompts = selectedLanguage === 'Hindi' ? HINDI_PHONEME_PROMPTS : ENGLISH_PHONEME_PROMPTS;
        const prompt = prompts.find(p => p.id === recording.promptId);
        if (!prompt) {
          throw new Error('Prompt not found');
        }

        // Create FormData
        const formData = new FormData();
        formData.append('audio', recording.audioBlob, 'recording.webm');
        formData.append('language', selectedLanguage);
        formData.append('accent', selectedAccent);
        formData.append('phoneme', prompt.symbol);
        formData.append('coachId', 'coach-' + Date.now()); // TODO: Replace with actual coach ID from auth
        
        response = await fetch(`${backendUrl}/api/phonemes/upload`, {
          method: 'POST',
          body: formData,
        });
      }

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Upload failed');
      }

      const result = await response.json();
      console.log('Upload successful:', result);
      
      // Mark as uploaded
      setRecordings(prev => 
        prev.map(r => r.id === recording.id ? { ...r, uploaded: true } : r)
      );
      
      setUploadedRecordingId(recording.id);
      setUploadStatus('✅ Upload successful! Your recording has been saved.');
      
      // Clear status after animation
      setTimeout(() => {
        setUploadStatus('');
        setUploadedRecordingId(null);
      }, 4000);
      
    } catch (error) {
      console.error('Upload error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setUploadStatus(`Upload failed: ${errorMessage}`);
      setTimeout(() => setUploadStatus(''), 5000);
    }
  };

  const deleteRecording = (recordingId: string) => {
    setRecordings(prev => prev.filter(r => r.id !== recordingId));
    if (currentRecording?.id === recordingId) {
      setCurrentRecording(null);
    }
  };

  return (
    <div className={`coach-mode ${recordingMode}-mode-active`}>
      <div className="coach-header">
        <h2>🎯 Coach Mode</h2>
        
        <div className="language-accent-selector">
          <div className="selector-group">
            <label htmlFor="language-select">Language:</label>
            <CustomDropdown
              id="language-select"
              value={selectedLanguage}
              options={LANGUAGES.map(lang => ({
                value: lang.code,
                label: lang.name
              }))}
              onChange={(value) => {
                setSelectedLanguage(value);
                // Reset accent to first option of new language
                setSelectedAccent(ACCENTS[value as keyof typeof ACCENTS]?.[0] || '');
              }}
            />
          </div>
          
          <div className="selector-group">
            <label htmlFor="accent-select">Accent:</label>
            <CustomDropdown
              id="accent-select"
              value={selectedAccent}
              options={(ACCENTS[selectedLanguage as keyof typeof ACCENTS] || []).map(accent => ({
                value: accent,
                label: accent
              }))}
              onChange={(value) => setSelectedAccent(value)}
            />
          </div>
          
          <div className="selector-group">
            <label>Mode:</label>
            <div className="mode-radio-group">
              <label className="mode-radio-label">
                <input
                  type="radio"
                  name="recordingMode"
                  value="phoneme"
                  checked={recordingMode === 'phoneme'}
                  onChange={(e) => setRecordingMode('phoneme')}
                  className="mode-radio-input"
                />
                <span className="mode-radio-custom">
                  <span className="mode-radio-text">🔤 Phoneme</span>
                </span>
              </label>
              <label className="mode-radio-label">
                <input
                  type="radio"
                  name="recordingMode"
                  value="sentence"
                  checked={recordingMode === 'sentence'}
                  onChange={(e) => setRecordingMode('sentence')}
                  className="mode-radio-input"
                />
                <span className="mode-radio-custom">
                  <span className="mode-radio-text">📝 Sentence</span>
                </span>
              </label>
            </div>
          </div>
        </div>
        
        {uploadStatus && (
          <div className={`upload-status ${uploadStatus.includes('failed') ? 'error' : 'success'}`}>
            {uploadStatus}
          </div>
        )}
      </div>

      <div className="coach-content">
        {recordingMode === 'phoneme' ? (
          <div className="prompt-section">
            <h3>Select a Sound to Record</h3>
            <div className="prompt-grid">
              {(selectedLanguage === 'Hindi' ? HINDI_PHONEME_PROMPTS : ENGLISH_PHONEME_PROMPTS).map(prompt => (
                <div
                  key={prompt.id}
                  className={`prompt-card ${selectedPrompt.id === prompt.id ? 'selected' : ''}`}
                  onClick={() => setSelectedPrompt(prompt)}
                >
                  {selectedLanguage === 'Hindi' && (prompt as any).devanagari && (
                    <div className="prompt-devanagari" style={{ fontSize: '24px', fontWeight: 'bold' }}>
                      {(prompt as any).devanagari}
                    </div>
                  )}
                  <div className="prompt-symbol">{prompt.symbol}</div>
                  <div className="prompt-example">{prompt.example}</div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="prompt-section">
            <h3>Select a Sentence to Record</h3>
            {selectedLanguage === 'Hindi' ? (
              <div className="sentence-grid">
                {hindiSentences.map(sentence => (
                  <div
                    key={sentence.id}
                    className={`sentence-card ${selectedSentence?.id === sentence.id ? 'selected' : ''}`}
                    onClick={() => setSelectedSentence(sentence)}
                    style={{
                      padding: '15px',
                      margin: '10px',
                      border: '2px solid #ddd',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      backgroundColor: selectedSentence?.id === sentence.id ? '#e8f4ff' : 'white'
                    }}
                  >
                    <div style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '5px' }}>{sentence.hindi}</div>
                    <div style={{ fontSize: '16px', color: '#666', marginBottom: '5px' }}>{sentence.transliteration}</div>
                    <div style={{ fontSize: '14px', color: '#888', marginBottom: '5px' }}>{sentence.english}</div>
                    <div style={{ fontSize: '12px', color: '#4CAF50', fontWeight: 'bold' }}>{sentence.difficulty}</div>
                  </div>
                ))}
              </div>
            ) : selectedLanguage === 'English' ? (
              <div className="sentence-grid">
                {englishSentences.map(sentence => (
                  <div
                    key={sentence.id}
                    className={`sentence-card ${selectedSentence?.id === sentence.id ? 'selected' : ''}`}
                    onClick={() => setSelectedSentence(sentence)}
                    style={{
                      padding: '15px',
                      margin: '10px',
                      border: '2px solid #ddd',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      backgroundColor: selectedSentence?.id === sentence.id ? '#e8f4ff' : 'white'
                    }}
                  >
                    <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '5px' }}>{sentence.text}</div>
                    <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>Target phonemes: {sentence.targetPhonemes.join(', ')}</div>
                    <div style={{ fontSize: '12px', color: '#4CAF50', fontWeight: 'bold' }}>{sentence.difficulty} • {sentence.type}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="info-message" style={{ padding: '20px', textAlign: 'center', backgroundColor: '#f0f0f0', borderRadius: '8px' }}>
                <p>Sentence recording is available for English and Hindi.</p>
                <p>Please select English or Hindi from the language dropdown to continue.</p>
              </div>
            )}
          </div>
        )}

        <div className="recording-section">
          {recordingMode === 'phoneme' ? (
            <div className="current-prompt">
              <h3>Current Sound</h3>
              <div className="prompt-display">
                <div className="prompt-main">
                  <span className="prompt-symbol-large">{selectedPrompt.symbol}</span>
                  <span className="prompt-example-large">"{selectedPrompt.example}"</span>
                </div>
                <p className="prompt-instruction">{selectedPrompt.instruction}</p>
              </div>
            </div>
          ) : selectedSentence ? (
            <div className="current-prompt">
              <h3>Current Sentence</h3>
              <div className="prompt-display">
                {selectedLanguage === 'Hindi' ? (
                  <>
                    <div className="sentence-main" style={{ textAlign: 'center', padding: '20px' }}>
                      <div style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '10px' }}>{selectedSentence.hindi}</div>
                      <div style={{ fontSize: '20px', color: '#666', marginBottom: '10px' }}>{selectedSentence.transliteration}</div>
                      <div style={{ fontSize: '16px', color: '#888', marginBottom: '10px' }}>{selectedSentence.english}</div>
                    </div>
                    <p style={{ textAlign: 'center', color: '#4CAF50', fontWeight: 'bold' }}>Difficulty: {selectedSentence.difficulty}</p>
                    {selectedSentence.referenceAudioUrl && (
                      <div className="reference-audio" style={{ textAlign: 'center', marginTop: '15px' }}>
                        <p>Reference pronunciation:</p>
                        <audio controls src={selectedSentence.referenceAudioUrl} />
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="sentence-main" style={{ textAlign: 'center', padding: '20px' }}>
                      <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '15px' }}>{selectedSentence.text}</div>
                      <div style={{ fontSize: '16px', color: '#666', marginBottom: '10px' }}>Target phonemes: {selectedSentence.targetPhonemes?.join(', ')}</div>
                    </div>
                    <p style={{ textAlign: 'center', color: '#4CAF50', fontWeight: 'bold' }}>
                      Difficulty: {selectedSentence.difficulty} • Type: {selectedSentence.type}
                    </p>
                  </>
                )}
              </div>
            </div>
          ) : (
            <div className="current-prompt">
              <h3>No Sentence Selected</h3>
              <p style={{ textAlign: 'center', padding: '20px' }}>Please select a sentence from above to start recording.</p>
            </div>
          )}

          <div className="recording-controls">
            <div className="video-toggle">
              <label>
                <input
                  type="checkbox"
                  checked={isVideoEnabled}
                  onChange={(e) => setIsVideoEnabled(e.target.checked)}
                  disabled={isRecording}
                />
                Enable Video Recording (for lip position)
              </label>
            </div>

            {isVideoEnabled && (
              <div className="video-preview">
                <video ref={videoRef} autoPlay muted />
              </div>
            )}

            <div className="record-buttons">
              {!isRecording ? (
                <button className="record-btn start" onClick={startRecording}>
                  🔴 Start Recording
                </button>
              ) : (
                <button className="record-btn stop" onClick={stopRecording}>
                  ⏹️ Stop Recording
                </button>
              )}
            </div>
          </div>

          {currentRecording && (
            <div className={`current-recording-preview ${uploadedRecordingId === currentRecording.id ? 'just-uploaded' : ''}`}>
              <h3>Latest Recording</h3>
              <audio controls src={currentRecording.audioUrl} />
              {currentRecording.videoUrl && (
                <video controls src={currentRecording.videoUrl} />
              )}
              <div className="recording-actions">
                <button 
                  className="upload-btn"
                  onClick={() => uploadRecording(currentRecording)}
                  disabled={currentRecording.uploaded}
                >
                  {currentRecording.uploaded ? '✅ Uploaded Successfully' : '☁️ Upload Recording'}
                </button>
                <button 
                  className="delete-btn"
                  onClick={() => deleteRecording(currentRecording.id)}
                >
                  🗑️ Delete
                </button>
              </div>
              {uploadedRecordingId === currentRecording.id && (
                <div className="upload-success-overlay">
                  <div className="success-checkmark">✅</div>
                  <div className="success-text">Uploaded!</div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="recordings-history">
          <h3>Your Recordings ({recordings.length})</h3>
          <div className="recordings-list">
            {recordings.map(recording => {
              const isPhoneme = !recording.promptId.startsWith('sentence_');
              let displayData: any = null;
              
              if (isPhoneme) {
                const prompts = selectedLanguage === 'Hindi' ? HINDI_PHONEME_PROMPTS : ENGLISH_PHONEME_PROMPTS;
                displayData = prompts.find(p => p.id === recording.promptId);
              } else {
                const sentenceId = recording.promptId.replace('sentence_', '');
                // Handle both numeric IDs (from JSON) and string IDs (from database)
                displayData = hindiSentences.find(s => s.id?.toString() === sentenceId) || 
                              englishSentences.find(s => s.id?.toString() === sentenceId);
              }
              
              return (
                <div key={recording.id} className={`recording-row ${uploadedRecordingId === recording.id ? 'just-uploaded' : ''}`}>
                  <div className="recording-phoneme">
                    {isPhoneme ? (
                      <>
                        <span className="phoneme-symbol">{displayData?.symbol}</span>
                        <span className="phoneme-example">{displayData?.example}</span>
                      </>
                    ) : (
                      <>
                        <span style={{ fontSize: '14px', fontWeight: 'bold' }}>
                          {displayData?.hindi || displayData?.text}
                        </span>
                        {displayData?.transliteration && (
                          <span style={{ fontSize: '12px', color: '#666', marginLeft: '10px' }}>
                            {displayData.transliteration}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                  
                  <audio controls src={recording.audioUrl} className="recording-player" />
                  
                  {recording.videoUrl && (
                    <button 
                      className="video-btn"
                      onClick={() => {
                        // Open video in a modal or new window
                        window.open(recording.videoUrl, '_blank', 'width=400,height=300');
                      }}
                      title="View Video"
                    >
                      📹
                    </button>
                  )}
                  
                  <div className="recording-buttons">
                    {recording.uploaded ? (
                      <span className="uploaded-badge">✓ Uploaded</span>
                    ) : (
                      <button 
                        className="btn-upload"
                        onClick={() => uploadRecording(recording)}
                        title="Upload Recording"
                      >
                        ☁️
                      </button>
                    )}
                    <button 
                      className="btn-delete"
                      onClick={() => deleteRecording(recording.id)}
                      title="Delete Recording"
                    >
                      🗑️
                    </button>
                  </div>
                  
                  <span className="recording-timestamp">
                    {recording.timestamp.toLocaleTimeString()}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoachMode;